// src/Dashboard/pages/teacher/Events/EventsDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useEventsDashboard } from "./hooks/useEventsDashboard";

import DashboardEventsHeader from "./components/DashboardEventsHeader";
import EventsFiltersCard from "./components/EventsFiltersCard";
import EventsKpiCards from "./components/EventsKpiCards";
import EventsDataTable from "./components/EventsDataTable";
import EventsMobileCards from "./components/EventsMobileCards";
import EventsEmptyState from "./components/EventsEmptyState";
import EventDrawer from "./components/EventDrawer";

import EventDetailsModalEnseignants from "../../../../pages/PagesEnseignants/EventsPage/EventDetailsModalEnseignants";
import GroupMessageModal from "../../../../pages/PagesEnseignants/EventsPage/GroupMessageModal";
import UploadEventsModal from "../../../../pages/PagesEnseignants/EventsPage/UploadEventsModal";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Événements
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants du
              club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function EventsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    events,
    filteredEvents,
    paginatedEvents,
    stats,

    filters,
    setFilters,
    page,
    setPage,
    totalPages,
    pageSize,

    shortnameClub,
    typeOptions,
    levelOptions,
    categoryOptions,

    loading,
    saving,
    deletingId,
    togglingIds,
    uploadingImage,

    drawerOpen,
    drawerMode,
    currentEvent,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    uploadModalOpen,
    openUploadModal,
    closeUploadModal,

    detailsEvent,
    openDetailsModal,
    closeDetailsModal,

    groupMessageOpen,
    groupMessageEvent,
    openGroupMessageModal,
    closeGroupMessageModal,

    previewOpen,
    previewSrc,
    openPreview,
    closePreview,

    handleCreateEvent,
    handleUpdateEvent,
    handleDeleteEvent,
    handleToggleActive,
    handleToggleRegistration,
    handleUploadEventImage,
    handleExportFiltered,
    refreshEvents,
  } = useEventsDashboard(user);

  const authorized = roleId === 1 || roleId === 3;

  if (!authorized) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardEventsHeader onAdd={openCreateDrawer} />

        <EventsFiltersCard
          filters={filters}
          setFilters={setFilters}
          loading={loading}
          typeOptions={typeOptions}
          levelOptions={levelOptions}
          categoryOptions={categoryOptions}
          onAdd={openCreateDrawer}
          onUpload={openUploadModal}
          onExport={handleExportFiltered}
          onRefresh={refreshEvents}
        />

        <EventsKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste événements
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Calendrier, inscriptions et affichage public
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Gérez les événements du club, l’ouverture des inscriptions,
                  l’affichage public, les participants et les invitations par
                  groupe.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Événements affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredEvents.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {events.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredEvents.length === 0 ? (
              <EventsEmptyState onAdd={openCreateDrawer} />
            ) : (
              <div className="mt-5">
                <EventsDataTable
                  events={paginatedEvents}
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  totalItems={filteredEvents.length}
                  pageSize={pageSize}
                  filters={filters}
                  setFilters={setFilters}
                  deletingId={deletingId}
                  togglingIds={togglingIds}
                  onPreview={openPreview}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteEvent}
                  onDetails={openDetailsModal}
                  onGroupMessage={openGroupMessageModal}
                  onToggleActive={handleToggleActive}
                  onToggleRegistration={handleToggleRegistration}
                />

                <EventsMobileCards
                  events={paginatedEvents}
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  totalItems={filteredEvents.length}
                  deletingId={deletingId}
                  togglingIds={togglingIds}
                  onPreview={openPreview}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteEvent}
                  onDetails={openDetailsModal}
                  onGroupMessage={openGroupMessageModal}
                  onToggleActive={handleToggleActive}
                  onToggleRegistration={handleToggleRegistration}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <EventDrawer
        isOpen={drawerOpen}
        mode={drawerMode}
        event={currentEvent}
        shortnameClub={shortnameClub}
        typeOptions={typeOptions}
        levelOptions={levelOptions}
        categoryOptions={categoryOptions}
        saving={saving}
        uploadingImage={uploadingImage}
        onClose={closeDrawer}
        onSubmit={
          drawerMode === "create" ? handleCreateEvent : handleUpdateEvent
        }
        onUploadImage={handleUploadEventImage}
      />

      <UploadEventsModal
        isOpen={uploadModalOpen}
        onClose={closeUploadModal}
        onUploaded={refreshEvents}
      />

      <EventDetailsModalEnseignants
        event={detailsEvent}
        isOpen={!!detailsEvent}
        onClose={closeDetailsModal}
      />

      <GroupMessageModal
        isOpen={groupMessageOpen}
        onClose={closeGroupMessageModal}
        event={groupMessageEvent}
        senderId={user?.userId || user?.id}
      />

      {previewOpen ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 p-4"
          onClick={(event) =>
            event.currentTarget === event.target && closePreview()
          }
          role="dialog"
          aria-modal="true"
          aria-label="Aperçu de l'image"
        >
          <button
            type="button"
            className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            onClick={closePreview}
            title="Fermer"
          >
            ×
          </button>

          <img
            src={previewSrc}
            alt="Aperçu événement"
            className="max-h-[90vh] max-w-[95vw] object-contain"
            onError={(event) => {
              event.currentTarget.src = "/img/no-image.jpg";
            }}
          />
        </div>
      ) : null}

      <ToastContainer />
    </section>
  );
}
